import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Accordion, AccordionSummary, AccordionDetails, Stack } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DataGrid } from '@mui/x-data-grid';
import { getDeviceParsed } from '../../api/apiClient';
import { useToast } from '../../hooks/useToast';

export default function ConfigParsedPage() {
  const { deviceId } = useParams();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ firmware: 'Unknown', createdAt: '' });
  const [sections, setSections] = useState({});

  const controllerRef = useRef(null);
  const inFlightRef = useRef(false);
  const promiseRef = useRef(null);

  const loadParsed = useCallback(async () => {
    console.log(`[ConfigParsedPage] loadParsed called, deviceId=${deviceId}, inFlight=${inFlightRef.current}`);
    if (!deviceId) return;
    if (inFlightRef.current && promiseRef.current) {
      console.log(`[ConfigParsedPage] Skipping - already in flight`);
      return promiseRef.current;
    }
    console.log(`[ConfigParsedPage] Starting new fetch for ${deviceId}`);
    const ctrl = new AbortController();
    controllerRef.current = ctrl;
    inFlightRef.current = true;
    setLoading(true);
    const p = getDeviceParsed(deviceId, { signal: ctrl.signal })
      .then((data) => {
        console.log('[ConfigParsedPage] Received data:', data);
        const parsedData = data?.parsed || {};
        console.log('[ConfigParsedPage] parsed:', parsedData);
        const sec = parsedData?.sections || {};
        console.log('[ConfigParsedPage] sections:', sec);
        // Build sections map of rows per section
        const normalized = {};
        Object.entries(sec).forEach(([sectionName, entries]) => {
          const rows = [];
          Object.entries(entries || {}).forEach(([key, value]) => {
            rows.push({
              id: `${sectionName}-${key}`,
              key,
              value: typeof value === 'object' ? JSON.stringify(value) : String(value),
            });
          });
          normalized[sectionName] = rows;
        });
        console.log('[ConfigParsedPage] normalized sections:', normalized);
        setSections(normalized);
        const unknown = Array.isArray(data?.unknown_keys) ? data.unknown_keys.length : (data?.unknown_keys || 0);
        setMeta({
          deviceId: data?.device_id,
          firmware: data?.firmware || 'Unknown',
          createdAt: data?.metadata?.created_at || data?.metadata?.createdAt || '',
          mapped: 0,
          unknown,
        });
      })
      .catch((e) => {
        if (e?.name === 'AbortError') return;
        console.error('Failed to load parsed config', e);
        toast.error('Failed to load parsed config');
      })
      .finally(() => {
        if (controllerRef.current === ctrl) {
          inFlightRef.current = false;
          setLoading(false);
        }
      });
    promiseRef.current = p;
    return p;
  }, [deviceId, toast]);

  useEffect(() => {
    if (deviceId) loadParsed();
    return () => {
      if (controllerRef.current) {
        try { controllerRef.current.abort(); } catch (_) {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const sectionNames = useMemo(() => Object.keys(sections || {}).sort(), [sections]);

  if (loading) {
    return (
      <Box p={3} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5">Parsed Configuration</Typography>
          <Typography variant="body2">
            Device: {deviceId} | Firmware: {meta.firmware || 'Unknown'} | Last Updated: {meta.createdAt || 'N/A'}
          </Typography>
        </Box>
        <Button variant="contained" onClick={loadParsed}>Reload</Button>
      </Stack>

      {sectionNames.length === 0 ? (
        <Typography>No configuration data available.</Typography>
      ) : (
        <Box sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {sectionNames.map((name) => (
            <Accordion key={name} defaultExpanded sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}> 
                <Typography sx={{ fontWeight: 600 }}>{name}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <Box sx={{ minWidth: 800 }}>
                    <DataGrid
                      rows={sections[name] || []}
                      columns={[
                        { field: 'key', headerName: 'Key', flex: 1, minWidth: 200 },
                        { field: 'value', headerName: 'Value', flex: 2, minWidth: 400 },
                      ]}
                      autoHeight
                      disableSelectionOnClick
                      pageSizeOptions={[10, 25, 50]}
                      initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
                    />
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Box>
  );
}
